/**
 * A store's CDT often writes the category before every section: "BATH TISSUE - Charmin",
 * "BATH TISSUE - Cottonelle". The category manager knows the category; what they read is the
 * brand. The prefix is found in the data — the "<category> - " every section value shares —
 * never guessed from the wording, and it leaves ids and keys alone: only displayed text drops it.
 */
export const categoryPrefixOf = (sections: Iterable<string | null | undefined>): string | null => {
  let prefix: string | null = null;
  const distinct = new Set<string>();
  for (const section of sections) {
    if (!section) continue;
    const match = /^(.+?) - \S/.exec(section);
    if (!match) return null;
    const own = `${match[1]} - `;
    if (prefix !== null && prefix !== own) return null;
    prefix = own;
    distinct.add(section);
  }
  // One section alone could be a name that happens to hold " - ".
  return distinct.size >= 2 ? prefix : null;
};

/** Where a section's name may start: the start of a line, after a separator (" › ", " → ",
 *  "(", ", ", ": ", "; ") or an opening quote or question mark ("¿"), or after a lowercase word
 *  of the sentence around it ("splits the …", "y …"). After a capitalised word or inside a
 *  word, the prefix is part of a product's own name. */
const VALUE_START = String.raw`(^| › | → |\(|, |: |; |["'“‘«¿]|(?:^|[^\p{L}\p{N}])\p{Ll}+ )`;

/** The text as a category manager reads it: every section named without the category. */
export const withoutCategory = (text: string, prefix: string | null): string =>
  prefix
    ? text.replace(
        new RegExp(VALUE_START + prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gmu"),
        "$1",
      )
    : text;

/** A CDT value that names no block ("Unassigned CDT1", "Others"), as the tile reads a column. */
export const namesNoBlock = (value: string): boolean =>
  /^(unspecified|unassigned\b.*|others?|n\/?a|none|unknown|—|-)?$/i.test(value.trim());

/** The plural key's suffix, i18next style: "_one" at one, "_other" otherwise (en, es). */
export const pluralOf = (count: number) => (count === 1 ? "one" : "other");
